import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useMediaQuery } from "@mui/material";

export type ThemeMode = "light" | "dark" | "system";

interface ThemeColors {
	primary: string;
	secondary: string;
}

interface ThemeContextValue {
	/** O modo selecionado pelo usuário: light, dark ou system */
	themeMode: ThemeMode;
	/** O modo efetivo resolvido (sem "system") */
	resolvedMode: "light" | "dark";
	/** Cores customizadas da interface */
	colors: ThemeColors;
	/** Atualiza o modo de tema */
	setThemeMode: (mode: ThemeMode) => void;
	/** Atualiza a cor primária */
	setPrimaryColor: (color: string) => void;
	/** Atualiza a cor secundária */
	setSecondaryColor: (color: string) => void;
	/** Reseta as cores para os padrões */
	resetColors: () => void;
}

const STORAGE_KEYS = {
	mode: "ekeeps-theme-mode",
	primary: "ekeeps-theme-primary",
	secondary: "ekeeps-theme-secondary",
} as const;

const DEFAULT_PRIMARY = "#1DB954";
const DEFAULT_SECONDARY = "#FF003C";

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

function getStoredValue<T extends string>(key: string, fallback: T): T {
	try {
		const stored = localStorage.getItem(key);
		return (stored as T) ?? fallback;
	} catch {
		return fallback;
	}
}

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
	const prefersDark = useMediaQuery("(prefers-color-scheme: dark)");

	const [themeMode, setThemeModeState] = useState<ThemeMode>(() => getStoredValue(STORAGE_KEYS.mode, "system"));
	const [primaryColor, setPrimaryColorState] = useState<string>(() => getStoredValue(STORAGE_KEYS.primary, DEFAULT_PRIMARY));
	const [secondaryColor, setSecondaryColorState] = useState<string>(() => getStoredValue(STORAGE_KEYS.secondary, DEFAULT_SECONDARY));

	const resolvedMode: "light" | "dark" = useMemo(() => {
		if (themeMode === "system") return prefersDark ? "dark" : "light";
		return themeMode;
	}, [themeMode, prefersDark]);

	const setThemeMode = useCallback((mode: ThemeMode) => {
		setThemeModeState(mode);
		localStorage.setItem(STORAGE_KEYS.mode, mode);
	}, []);

	const setPrimaryColor = useCallback((color: string) => {
		setPrimaryColorState(color);
		localStorage.setItem(STORAGE_KEYS.primary, color);
	}, []);

	const setSecondaryColor = useCallback((color: string) => {
		setSecondaryColorState(color);
		localStorage.setItem(STORAGE_KEYS.secondary, color);
	}, []);

	const resetColors = useCallback(() => {
		setPrimaryColor(DEFAULT_PRIMARY);
		setSecondaryColor(DEFAULT_SECONDARY);
	}, [setPrimaryColor, setSecondaryColor]);

	// Aplica CSS custom properties no :root para cores secundárias
	useEffect(() => {
		document.documentElement.style.setProperty("--ekeeps-secondary-color", secondaryColor);
	}, [secondaryColor]);

	const value = useMemo<ThemeContextValue>(
		() => ({
			themeMode,
			resolvedMode,
			colors: { primary: primaryColor, secondary: secondaryColor },
			setThemeMode,
			setPrimaryColor,
			setSecondaryColor,
			resetColors,
		}),
		[themeMode, resolvedMode, primaryColor, secondaryColor, setThemeMode, setPrimaryColor, setSecondaryColor, resetColors],
	);

	return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useThemeSettings = (): ThemeContextValue => {
	const ctx = useContext(ThemeContext);
	if (!ctx) {
		throw new Error("useThemeSettings must be used within a ThemeProvider");
	}
	return ctx;
};

export { DEFAULT_PRIMARY, DEFAULT_SECONDARY };
